import { IllegalArgumentError } from "@archipad-js/core/error";
import { LoggerManager } from "@archipad-js/core/logger";
import { AttributeTypeConf, ExportKind, PropertyDescription, ValuePropertyType } from "./types";

export const logger = LoggerManager.getLogger("ORM");

export function convertType(type: AttributeTypeConf): ValuePropertyType | null {
    switch (type) {
        case "int16":
            return "number";
        case "int32":
            return "number";
        case "int64":
            return "string";
        case "decimal":
            return "number";
        case "double":
            return "number";
        case "float":
            return "number";
        case "string":
            return "string";
        case "bool":
            return "boolean";
        case "date":
            return "date";
        case "binary":
            return "string";
        case "transformable":
            return "object";
        case "undefined":
            return "object";
        case "uuid":
            return "string";
        default:
            return null;
    }
}

export function convertBoolean(value: unknown, defaultValue: boolean): boolean {
    return value == null ? defaultValue : Boolean(value);
}

export const kExportKindValueByName: Map<string, ExportKind> = new Map([
    ["no", ExportKind.no],
    ["yes", ExportKind.yes],
    ["clearOnExport", ExportKind.clearOnExport],
    ["cache", ExportKind.cache],
]);

export const kExportKindExportableByValue: Record<ExportKind, boolean> = {
    [ExportKind.no]: false,
    [ExportKind.yes]: true,
    [ExportKind.clearOnExport]: true,
    [ExportKind.cache]: false,
};

export function convertExportKind(
    value: string | boolean | number | null | undefined,
    defaultValue: ExportKind,
): ExportKind {
    let result: ExportKind;

    if (value == null) {
        result = defaultValue;
    } else if (typeof value === "boolean" || typeof value === "number") {
        result = value ? ExportKind.yes : ExportKind.no;
    } else if (typeof value === "string") {
        const numValue = kExportKindValueByName.get(value);
        if (numValue) {
            result = numValue;
        } else {
            logger.error("unknown ExportKind | value:", value);
            result = ExportKind.no;
        }
    } else {
        logger.error("unknown ExportKind | value:", value);
        result = ExportKind.no;
    }

    return result;
}

export function getPropertyDefaultValue(prop: PropertyDescription): unknown {
    if (prop.relation === true) {
        // initialize multiple relations
        return prop.single ? null : [];
    }

    if (prop.defaultValue == null) {
        // no default value provided
        if (prop.optional) {
            return null;
        } else {
            switch (prop.type) {
                case "number":
                    return 0;
                case "boolean":
                    return false;
                case "date":
                    return new Date(0);
                case "string":
                    return "";
                default:
                    throw new IllegalArgumentError("Can't provide a defaultValue for: " + JSON.stringify(prop));
            }
        }
    }

    let defaultValue = prop.defaultValue;

    if (typeof defaultValue == "object" && "func:" in defaultValue && defaultValue["func:"]) {
        throw new IllegalArgumentError("Unexpected defaultValue: " + JSON.stringify(prop));
    }

    switch (prop.type) {
        case "number":
            if (typeof defaultValue == "number") {
                break;
            }
            throw new IllegalArgumentError("Unexpected defaultValue: " + JSON.stringify(prop));
        case "boolean":
            if (typeof defaultValue == "number") {
                defaultValue = defaultValue !== 0;
                break;
            }
            if (typeof defaultValue == "boolean") {
                break;
            }
            throw new IllegalArgumentError("Unexpected defaultValue: " + JSON.stringify(prop));
        case "string":
            if (typeof defaultValue == "string") {
                break;
            }
            if (typeof defaultValue == "number" && prop.originType == "int64") {
                if (defaultValue < -1 || defaultValue > 1) {
                    throw new IllegalArgumentError(
                        "Unexpected defaultValue on int64 (only string or -1, 0, 1 are allowed): " +
                            JSON.stringify(prop),
                    );
                }
                defaultValue = defaultValue.toString();
                break;
            }
            throw new IllegalArgumentError("Unexpected defaultValue: " + JSON.stringify(prop));
        case "date":
            if (typeof defaultValue == "number") {
                defaultValue = new Date(defaultValue);
                break;
            }
            throw new IllegalArgumentError("Unexpected defaultValue: " + JSON.stringify(prop));
        default:
            throw new IllegalArgumentError("Unexpected defaultValue: " + JSON.stringify(prop));
    }

    return defaultValue;
}

/**
 * Converts a UUID to a decimal string.
 *
 * @param uuid The UUID to convert.
 * @returns A decimal string representation of the UUID.
 *
 * @example
 * ```ts
 * const decimalString = uuidToDecimalString("00000000-0000-0000-1451-ccf921dd229d");
 * console.log(decimalString); // "1464176724259185309"
 * ```
 */
export function uuidToDecimalString(uuid: string): string {
    const hex = uuid.replace(/-/g, "");
    return BigInt(`0x${hex}`).toString();
}

/**
 * Converts a decimal string to a UUID.
 *
 * @param decimalString The decimal string to convert.
 * @returns A UUID representation of the decimal string.
 * @example
 * ```ts
 * const uuid = decimalStringToUuid("1464176724259185309");
 * console.log(uuid); // "00000000-0000-0000-1451-ccf921dd229d"
 * ```
 */
export function decimalStringToUuid(decimalString: string): string {
    const hex = BigInt(decimalString).toString(16).padStart(32, "0");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
